import React, { useState, useCallback } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";

import "../src/styles/main.css";
import "../src/styles/Footer.css";
import "../src/styles/Header.css";

// ── Common layout ─────────────────────────────────────────────────────────────
import Header   from "./components/common/Header";
import ScrollToTop from "./components/common/ScrollToTop";
import Footer   from "./components/common/Footer";
import Chatbot  from "./components/common/Chatbot";

// ── Home ──────────────────────────────────────────────────────────────────────
import HomePageContent from "./components/home/HomePageContent";

// ── User ──────────────────────────────────────────────────────────────────────
import UserLogin      from "./components/user/UserLogin";
import UserSignup     from "./components/user/UserSignup";
import UserDashboard  from "./components/user/UserDashboard";
import TrackOrder     from "./components/user/TrackOrder";
import { useLogoutSync } from "./components/user/useLogoutSync";
import { CartProvider }  from "./components/user/CartContext";

// ── Partner (Labour) ──────────────────────────────────────────────────────────
import LabourPage             from "./components/partner/LabourPage";
import LabourLogin            from "./components/partner/LabourLogin";
import LabourSignup           from "./components/partner/LabourSignup";
import LabourDashboard        from "./components/partner/LabourDashboard";
import LabourRegistrationForm from "./components/partner/LabourRegistrationForm";

// ── Agent ─────────────────────────────────────────────────────────────────────
import AgentLogin            from "./components/agent/AgentLogin";
import AgentSignup           from "./components/agent/AgentSignup";
import AgentDashboard        from "./components/agent/AgentDashboard";
import AgentRegistrationPage from "./components/agent/AgentRegistrationPage";

// ── Service listing pages ─────────────────────────────────────────────────────
import AllServices       from "./components/services/AllServices";
import HomeServices      from "./components/services/HomeServices";
import InstantServiceDetailPage from "./components/services/InstantServiceDetailPage";
import TechnicalServices from "./components/services/TechnicalServices";
import CaretakerServices from "./components/services/CaretakerServices";
import OutdoorServices   from "./components/services/OutdoorServices";
import GardeningServiceComingSoon from "./components/workerspage/outdoor/GardeningServiceComingSoon";
import CarWashServiceComingSoon from "./components/workerspage/outdoor/CarWashServiceComingSoon";
import TransportServicesComingSoon from "./components/services/TransportServicesComingSoon";
import SpecialServices   from "./components/services/SpecialServices";
import DeepCleaningPage  from "./components/services/special/DeepCleaningPage";
import HomeUtilityCleaningPage from "./components/services/special/HomeUtilityCleaningPage";
import RapidCleaningPage from "./components/services/special/RapidCleaningPage";
import BalconyTerraceCleaningPage from "./components/services/special/BalconyTerraceCleaningPage";
import BedroomWardrobeCleaningPage from "./components/services/special/BedroomWardrobeCleaningPage";
import MoveInMoveOutCleaningPage from "./components/services/special/MoveInMoveOutCleaningPage";
import CorporateServices  from "./components/services/CorporateServices";
import SubscriptionPlans from "./components/membership/SubscriptionPlans";
import MembershipCheckout from "./components/membership/MembershipCheckout";
import SilverPackageCheckout from "./components/membership/SilverPackageCheckout";
import PlatinumPackageCheckout from "./components/membership/PlatinumPackageCheckout";
import PlatinumPlusPackageCheckout from "./components/membership/PlatinumPlusPackageCheckout";
import SilverPlusPackageCheckout from "./components/membership/SilverPlusPackageCheckout";
import ShoppingPage      from "./components/services/ShoppingPage";
import ScheduleBookingPage from "./components/booking/ScheduleBookingPage";
import ConsultationBookingPage from "./components/booking/ConsultationBookingPage";
import FanBookingPage from "./components/booking/FanBookingPage";
import SwitchSocketBookingPage from "./components/booking/SwitchSocketBookingPage";
import ApplianceBookingPage from "./components/booking/ApplianceBookingPage";
import CctvBookingPage from "./components/booking/CctvBookingPage";
import DoorbellSecurityBookingPage from "./components/booking/DoorbellSecurityBookingPage";
import InverterUpsBookingPage from "./components/booking/InverterUpsBookingPage";
import LightBookingPage from "./components/booking/LightBookingPage";
import MCBFuseBookingPage from "./components/booking/MCBFuseBookingPage";
import StabilizerBookingPage from "./components/booking/StabilizerBookingPage";
import SubmeterWaterSensorBookingPage from "./components/booking/SubmeterWaterSensorBookingPage";
import SwitchboardBookingPage from "./components/booking/SwitchboardBookingPage";
import WiringBookingPage from "./components/booking/WiringBookingPage";

// ── Checkout & Payment ────────────────────────────────────────────────────────
import ScheduleSummaryPage        from "./components/checkout/ScheduleSummaryPage";
import InstantSummaryPage         from "./components/checkout/InstantSummaryPage";
import PaymentPage                from "./components/checkout/PaymentPage";
import InstantOrderTracking       from "./components/checkout/InstantOrderTracking";
import InstantReschedulePage      from "./components/checkout/InstantReschedulePage";
import PaymentSubscriptionProcess from "./components/checkout/PaymentSubscriptionProcess";
import OnlinePayment              from "./components/checkout/OnlinePayment";

// ── Info / Static pages ───────────────────────────────────────────────────────
import AboutUs           from "./components/info/AboutUs";
import CancellationPolicy from "./components/info/CancellationPolicy";
import RefundPolicy      from "./components/info/RefundPolicy";
import PrivacyPolicy     from "./components/info/PrivacyPolicy";
import TermsConditions   from "./components/info/TermsConditions";
import CompanyPolicy     from "./components/info/CompanyPolicy";
import InvestorAgreementPolicy from "./components/info/InvestorAgreementPolicy";
import RulesRegulations  from "./components/info/RulesRegulations";
import ServicePartnerTerms from "./components/info/ServicePartnerTerms";
import GrievanceRedressalPolicy from "./components/info/GrievanceRedressalPolicy";
import ContactUs         from "./components/info/ContactUs";
import LegalInformation  from "./components/info/LegalInformation";
import FaqPage           from "./components/info/FaqPage";
import HowItWorks        from "./components/info/HowItWorks";
import BlogPage          from "./components/info/BlogPage";
import SleepingPartnerPage from "./components/info/SleepingPartnerPage";
import Subscription      from "./components/info/Subscription";
import UnderMaintenance  from "./components/info/under-maintenance";

// ── Caretaker service pages ───────────────────────────────────────────────────
import BabyCaretaker      from "./components/workerspage/caretaker/BabyCaretaker";
import ElderCare          from "./components/workerspage/caretaker/ElderCare";
import PatientCare        from "./components/workerspage/caretaker/PatientCare";
import HomeNursingSupport from "./components/workerspage/caretaker/HomeNursingSupport";
import GeneralCaretaker from "./components/workerspage/caretaker/GeneralCaretaker";
import PetCaretaker       from "./components/workerspage/caretaker/PetCaretaker";

// ── Home service pages ────────────────────────────────────────────────────────
import Cleaning          from "./components/workerspage/home/Cleaning";
import Cooking           from "./components/workerspage/home/Cooking";
import HouseKeeping      from "./components/workerspage/home/HouseKeeping";
import BathRoomCleaning  from "./components/workerspage/home/BathRoomCleaning";
import KitchenRoomCleaning from "./components/workerspage/home/KitchenRoomCleaning";
import BalconyCleaning   from "./components/workerspage/home/BalconyCleaning";
import SofaCushionCleaning from "./components/workerspage/home/SofaCushionCleaning";
import FloorMopping      from "./components/workerspage/home/FloorMopping";
import WindowGlassCleaning from "./components/workerspage/home/WindowGlassCleaning";
import PackingUnpacking  from "./components/workerspage/home/PackingUnpacking";
import TankCleaner       from "./components/workerspage/home/TankCleaner";
import CarDriver         from "./components/workerspage/home/CarDriver";

// ── Technical service pages ───────────────────────────────────────────────────
import AcRepair             from "./components/workerspage/technical/AcRepair";
import TvRepair             from "./components/workerspage/technical/TvRepair";
import FridgeRepair         from "./components/workerspage/technical/FridgeRepair";
import WashingMachineRepair from "./components/workerspage/technical/WashingMachineRepair";
import PlumberService       from "./components/workerspage/technical/PlumberService";
import HomeAppliance        from "./components/workerspage/technical/HomeAppliance";

// ── Outdoor service pages ─────────────────────────────────────────────────────
import HelperConstruction from "./components/workerspage/outdoor/HelperConstruction";
import PackersMovers      from "./components/workerspage/outdoor/PackersMovers";
import DrinkingWater      from "./components/workerspage/outdoor/DrinkingWater";
import PaintingService    from "./components/workerspage/outdoor/PaintingService";

// ── Insurance service pages ───────────────────────────────────────────────────
import VehicleInsuranceAgent from "./components/workerspage/insurance/VehicleInsuranceAgent";
import LifeInsuranceAgent    from "./components/workerspage/insurance/LifeInsuranceAgent";

// ── Special service pages ─────────────────────────────────────────────────────
import SpecialServicePage from "./components/workerspage/special/SpecialServices";

// HomeAppliance is still reused for the living-room alias — Balcony now has
// its own dedicated page (BalconyCleaning.js), imported above.
const LivingRoomCleaning = HomeAppliance;

// Profile dashboard alias
const ProfileDashboard = UserDashboard;

// ── Auth helper ───────────────────────────────────────────────────────────────
const getCurrentUser = () => {
  try {
    return (
      JSON.parse(localStorage.getItem("currentUser")) ||
      JSON.parse(localStorage.getItem("user")) ||
      null
    );
  } catch {
    return null;
  }
};

function App() {
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const raw = localStorage.getItem("currentUser");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const handleLogout = useCallback(() => setCurrentUser(null), []);
  useLogoutSync(handleLogout);

  return (
    <CartProvider>
      <Router>
        <div>
          <ScrollToTop />
          <Header />

          <Routes>
            {/* ── Home ──────────────────────────────────────────────────── */}
            <Route path="/"         element={<HomePageContent />} />
            <Route path="/shopping" element={<ShoppingPage />} />

            {/* ── Info / static pages ───────────────────────────────────── */}
            <Route path="/about-us"          element={<AboutUs />} />
            <Route path="/cancellation-policy" element={<CancellationPolicy />} />
            <Route path="/refund-policy"       element={<RefundPolicy />} />
            <Route path="/privacy-policy"      element={<PrivacyPolicy />} />
            <Route path="/terms-conditions"    element={<TermsConditions />} />
            <Route path="/company-policy"              element={<CompanyPolicy />} />
            <Route path="/investor-agreement-policy"   element={<InvestorAgreementPolicy />} />
            <Route path="/rules-regulations"           element={<RulesRegulations />} />
            <Route path="/service-partner-terms"       element={<ServicePartnerTerms />} />
            <Route path="/grievance-redressal-policy"  element={<GrievanceRedressalPolicy />} />
            <Route path="/contact-us"        element={<ContactUs />} />
            <Route path="/legal-information" element={<LegalInformation />} />
            <Route path="/faq"               element={<FaqPage />} />
            <Route path="/how-it-works"      element={<HowItWorks />} />
            <Route path="/blog"              element={<BlogPage />} />
            <Route path="/SleepingPartnerPage" element={<SleepingPartnerPage />} />
            <Route path="/subscription"        element={<Subscription />} />
            <Route path="/underMaintenance"    element={<UnderMaintenance />} />

            {/* ── User ──────────────────────────────────────────────────── */}
            <Route path="/user/user-login"      element={<UserLogin />} />
            <Route path="/track-order"          element={<TrackOrder />} />
            <Route path="/user/user-signup"     element={<UserSignup />} />
            <Route path="/user/user-dashboard"  element={<UserDashboard />} />
            <Route path="/profile-dashboard"    element={<ProfileDashboard currentUser={currentUser} />} />
            <Route path="/user/profile"         element={<ProfileDashboard currentUser={currentUser} />} />

            {/* ── Agent ─────────────────────────────────────────────────── */}
            <Route path="/AgentLogin"            element={<AgentLogin />} />
            <Route path="/AgentSignup"           element={<AgentSignup />} />
            <Route path="/AgentDashboard"        element={<AgentDashboard />} />
            <Route path="/AgentRegistrationPage" element={<AgentRegistrationPage />} />

            {/* ── Partner / Labour ──────────────────────────────────────── */}
            <Route path="/labour/labour-login"             element={<LabourLogin />} />
            <Route path="/labour/labour-signup"            element={<LabourSignup />} />
            <Route path="/labour/labour-dashboard"         element={<LabourDashboard />} />
            <Route path="/LabourPage"                      element={<LabourPage />} />
            <Route path="/labour/labour-registration-form" element={<LabourRegistrationForm />} />

            {/* ── Service listing pages ─────────────────────────────────── */}
            <Route path="/services/all-services"        element={<AllServices />} />
            <Route path="/services/instant-home-services" element={<HomeServices />} />
            <Route path="/service/home-service/instant/:serviceId" element={<InstantServiceDetailPage />} />
            <Route path="/services/technical-services"  element={<TechnicalServices />} />
            <Route path="/services/technical-services/consultation" element={<ConsultationBookingPage />} />
            <Route path="/services/technical-services/consultation/:bookingId" element={<ConsultationBookingPage />} />
            <Route path="/services/caretaker-services"  element={<CaretakerServices />} />
            <Route path="/services/outdoor-services"    element={<OutdoorServices />} />
            <Route path="/services/outdoor-services/gardening" element={<GardeningServiceComingSoon />} />
            <Route path="/services/outdoor-services/carwash"   element={<CarWashServiceComingSoon />} />
            <Route path="/services/special-services"    element={<SpecialServices />} />
            <Route path="/service/special-service/deep-cleaning" element={<DeepCleaningPage />} />
            <Route path="/service/special-service/kitchen-utility-cleaning" element={<HomeUtilityCleaningPage />} />
            <Route path="/service/special-service/rapid-cleaning" element={<RapidCleaningPage />} />
            <Route path="/service/special-service/balcony-terrace-cleaning" element={<BalconyTerraceCleaningPage />} />
            <Route path="/service/special-service/bedroom-wardrobe-cleaning" element={<BedroomWardrobeCleaningPage />} />
            <Route path="/service/special-service/move-in-move-out-cleaning" element={<MoveInMoveOutCleaningPage />} />
            <Route path="/services/transport-services"  element={<TransportServicesComingSoon />} />
            <Route path="/services/corporate-services"   element={<CorporateServices />} />
            <Route path="/subscription-plans" element={<SubscriptionPlans />} />
            <Route path="/subscription-plans/checkout/silver-package" element={<SilverPackageCheckout />} />
            <Route path="/subscription-plans/checkout/silver-plus-package" element={<SilverPlusPackageCheckout />} />
            <Route path="/subscription-plans/checkout/platinum-package" element={<PlatinumPackageCheckout />} />
            <Route path="/subscription-plans/checkout/platinum-plus-package" element={<PlatinumPlusPackageCheckout />} />
            <Route path="/subscription-plans/checkout/:planId" element={<MembershipCheckout />} />
            <Route path="/schedule-booking" element={<ScheduleBookingPage />} />
            <Route path="/schedule-booking/technical/fan" element={<FanBookingPage />} />
            <Route path="/schedule-booking/technical/switch-socket" element={<SwitchSocketBookingPage />} />
            <Route path="/schedule-booking/technical/appliance" element={<ApplianceBookingPage />} />
            <Route path="/schedule-booking/technical/cctv" element={<CctvBookingPage />} />
            <Route path="/schedule-booking/technical/doorbell-security" element={<DoorbellSecurityBookingPage />} />
            <Route path="/schedule-booking/technical/inverter-ups" element={<InverterUpsBookingPage />} />
            <Route path="/schedule-booking/technical/light" element={<LightBookingPage />} />
            <Route path="/schedule-booking/technical/mcb-fuse" element={<MCBFuseBookingPage />} />
            <Route path="/schedule-booking/technical/stabilizer" element={<StabilizerBookingPage />} />
            <Route path="/schedule-booking/technical/submeter-water-sensor" element={<SubmeterWaterSensorBookingPage />} />
            <Route path="/schedule-booking/technical/switchboard" element={<SwitchboardBookingPage />} />
            <Route path="/schedule-booking/technical/wiring" element={<WiringBookingPage />} />
            <Route path="/schedule-booking/:categorySlug" element={<ScheduleBookingPage />} />

            {/* ── Checkout ──────────────────────────────────────────────── */}
            <Route path="/cart-summary"              element={<ScheduleSummaryPage />} />
            <Route path="/checkout-schedule/summary" element={<ScheduleSummaryPage />} />
            <Route path="/checkout-instant/summary"  element={<InstantSummaryPage />} />
            <Route path="/payment"                element={<PaymentPage />} />
            <Route path="/instant-tracking/:orderId" element={<InstantOrderTracking />} />
            <Route path="/instant-tracking/:orderId/reschedule" element={<InstantReschedulePage />} />
            <Route path="/PaymentSubscriptionProcess" element={<PaymentSubscriptionProcess />} />
            <Route path="/OnlinePayment"          element={<OnlinePayment />} />

            {/* ── Caretaker service pages ───────────────────────────────── */}
            <Route path="/service/caretaker-service/baby-care"            element={<BabyCaretaker />} />
            <Route path="/service/caretaker-service/elder-care"           element={<ElderCare />} />
            <Route path="/service/caretaker-service/patient-care"         element={<PatientCare />} />
            <Route path="/service/caretaker-service/medical-care-representative" element={<HomeNursingSupport />} />
            <Route path="/service/caretaker-service/general-care" element={<GeneralCaretaker />} />
            {/* legacy paths */}
            <Route path="/service/care-service/elder-caretaker"   element={<ElderCare />} />
            <Route path="/service/care-service/pet-caretaker"     element={<PetCaretaker />} />
            <Route path="/service/care-service/patient-caretaker" element={<PatientCare />} />
            <Route path="/service/care-service/nursing-assistant" element={<HomeNursingSupport />} />

            {/* ── Home service pages ────────────────────────────────────── */}
            <Route path="/service/home-service/kitchen-cleaning"    element={<KitchenRoomCleaning />} />
            <Route path="/service/home-service/bathroom-cleaning"   element={<BathRoomCleaning />} />
            <Route path="/service/home-service/house-keeping"       element={<HouseKeeping />} />
            <Route path="/service/home-service/cooking"             element={<Cooking />} />
            <Route path="/service/home-service/car-driver"          element={<CarDriver />} />
            <Route path="/service/home-service/living-room-cleaning" element={<LivingRoomCleaning />} />
            <Route path="/service/home-service/balcony-cleaning"    element={<BalconyCleaning />} />
            <Route path="/service/home-service/sofa-cushion-cleaning" element={<SofaCushionCleaning />} />
            <Route path="/service/home-service/floor-mopping"       element={<FloorMopping />} />
            <Route path="/service/home-service/window-glass-cleaning" element={<WindowGlassCleaning />} />
            <Route path="/service/home-service/packing-unpacking"   element={<PackingUnpacking />} />
            <Route path="/service/home-service/tank-cleaning"       element={<TankCleaner />} />
            <Route path="/Cleaning"       element={<Cleaning />} />

            {/* ── Technical service pages ───────────────────────────────── */}
            <Route path="/service/technical-service/ac-repair"                element={<AcRepair />} />
            {/* Instant booking for Electrical/Technical was removed — this
                category now books only through the Schedule flow. */}
            <Route path="/service/technical-service/electrical-repair"        element={<Navigate to="/schedule-booking/technical" replace />} />
            <Route path="/service/technical-service/home-appliance-services"  element={<HomeAppliance />} />
            <Route path="/service/technical-service/tv-repair"                element={<TvRepair />} />
            <Route path="/service/technical-service/fridge-repair"            element={<FridgeRepair />} />
            <Route path="/service/technical-service/washing-machine-repair"   element={<WashingMachineRepair />} />
            <Route path="/service/technical-service/plumber-service"          element={<PlumberService />} />
            <Route path="/PlumberService" element={<PlumberService />} />

            {/* ── Outdoor / transport service pages ─────────────────────── */}
            <Route path="/service/out-door-service/helper-construction"    element={<HelperConstruction />} />
            <Route path="/service/out-door-service/drinking-water-supplier" element={<DrinkingWater />} />
            <Route path="/service/out-door-service/packers-movers"         element={<PackersMovers />} />
            <Route path="/service/painting-service"                         element={<PaintingService />} />

            {/* ── Insurance service pages ───────────────────────────────── */}
            <Route path="/service/insurance-service/vehicle-insurance-services" element={<VehicleInsuranceAgent />} />
            <Route path="/service/insurance-service/life-insurance-services"    element={<LifeInsuranceAgent />} />

            {/* ── Special service pages ─────────────────────────────────── */}
            <Route path="/service/spcecial-service" element={<SpecialServicePage />} />
          </Routes>

          <Footer />
          <Chatbot />
        </div>
      </Router>
    </CartProvider>
  );
}

export default App;
